import axios from 'axios'
import type { components } from '@/types/generated'
import { bffBaseURL, normalizeError } from './apiClient'

// ============================================================================
// Metrics API — Phase 60 Milestone 10
// ============================================================================

/**
 * Detail metrics for one position. Note the shape: per-position metrics live
 * NESTED under `.metrics` (days_open, days_to_first_candidate, ...); only
 * `total_candidates` is top-level. Typing the boundary (BUG-121 / B3) makes a
 * wrong-level read like `res.days_open` a compile error instead of a silent
 * `undefined` that `|| 0` used to mask as "0 días abierta".
 */
export type JobPostingMetricsDetail = components['schemas']['JobPostingMetricsDetail']

export async function getPositionMetrics(jobId: string): Promise<JobPostingMetricsDetail> {
  try {
    // Phase 100 Task 1.2: Corrected URL from /jobs/{id}/metrics (404) to /metrics/positions/{id}
    const response = await axios.get<JobPostingMetricsDetail>(
      `${bffBaseURL}/api/v1/metrics/positions/${jobId}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get metrics')
      throw new Error(message)
    }
    throw error
  }
}

export async function listPositionMetrics(params: Record<string, any> = {}) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/metrics/positions`,
      { params }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to list metrics')
      throw new Error(message)
    }
    throw error
  }
}

export async function listRecruiterMetrics(params: Record<string, any> = {}) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/metrics/recruiters`,
      { params }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to list recruiter metrics')
      throw new Error(message)
    }
    throw error
  }
}

export async function generateMetricsReport(request: any) {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/metrics/report`,
      request,
      { responseType: 'blob' }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to generate report')
      throw new Error(message)
    }
    throw error
  }
}
